import { getSiteConfig } from './siteConfigService';
import { meetingService, Meeting } from './meetingService';

export const WORKSHOP_2026_PATH = '/2026-workshop';

export function meetingCodeIsWorkshop2026(
  meetingCode: string | null | undefined,
  siteWorkshopCode: string | null | undefined
): boolean {
  if (!meetingCode?.trim() || !siteWorkshopCode?.trim()) return false;
  return meetingCode.trim().toUpperCase() === siteWorkshopCode.trim().toUpperCase();
}

export interface Workshop2026Context {
  meeting: Meeting | null;
  meetingCode: string | null;
  loadError: string | null;
}

export async function loadWorkshop2026Context(options?: { authed?: boolean }): Promise<Workshop2026Context> {
  const site = await getSiteConfig();
  const code = (site.workshop_2026_meeting_code || '').trim();
  if (!code) {
    return { meeting: null, meetingCode: null, loadError: null };
  }
  try {
    const meeting = options?.authed
      ? await meetingService.getMeetingByCodeAuthed(code)
      : await meetingService.getMeetingByCode(code);
    return { meeting, meetingCode: code.toUpperCase(), loadError: null };
  } catch (e) {
    return {
      meeting: null,
      meetingCode: code.toUpperCase(),
      loadError: e instanceof Error ? e.message : 'Failed to load workshop',
    };
  }
}

export function formatWorkshopSchedule(meeting: Meeting | null): string {
  const day = pluralizeScheduleDay(meeting?.schedule_day || 'Saturday');
  const time = formatTime(meeting?.schedule_start_time) || '7:15 AM';
  const tz = formatTzAbbrev(meeting?.timezone) || 'Pacific';
  return `${day}, ${time} ${tz}`;
}

function pluralizeScheduleDay(day: string): string {
  const trimmed = day.trim();
  if (!trimmed) return 'Saturdays';
  const lower = trimmed.toLowerCase();
  if (lower.endsWith('s')) return trimmed;
  return `${trimmed}s`;
}

/** First session date + recurring time (for Zoom block and list-sharing copy). */
export function formatWorkshopStartSchedule(meeting: Meeting | null): string {
  const time = formatTime(meeting?.schedule_start_time) || '7:15 AM';
  const tz = formatTzAbbrev(meeting?.timezone) || 'Pacific';
  const dateStr = meeting?.workshop_first_session_date;
  if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    try {
      const d = new Date(`${dateStr}T12:00:00`);
      const dayDate = d.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      return `${dayDate} · ${time} ${tz}`;
    } catch {
      /* fall through */
    }
  }
  const day = meeting?.schedule_day || 'Saturday';
  return `${day}, ${time} ${tz} (start date TBD)`;
}

function getWeekdayFromScheduleDay(day: string | undefined): number | null {
  const normalized = (day || 'Saturday').toLowerCase().replace(/s$/, '');
  const map: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return map[normalized] ?? null;
}

function parseSessionDate(dateStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatSessionDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getNextMeetingDate(meeting: Meeting | null, fromDate: Date): Date | null {
  const firstSession = parseSessionDate(meeting?.workshop_first_session_date || '');
  if (!firstSession) return null;

  const today = new Date(fromDate);
  today.setHours(0, 0, 0, 0);

  if (today < firstSession) return firstSession;

  const weekday = getWeekdayFromScheduleDay(meeting?.schedule_day);
  if (weekday === null) return firstSession;

  const next = new Date(today);
  const daysUntil = (weekday - next.getDay() + 7) % 7;
  next.setDate(next.getDate() + daysUntil);
  return next;
}

/** First or next meeting line for the Join live block. */
export function formatWorkshopMeetingLine(meeting: Meeting | null): string {
  const time = formatTime(meeting?.schedule_start_time) || '7:15 AM';
  const tz = formatTzAbbrev(meeting?.timezone) || 'Pacific';
  const timePart = `${time} ${tz}`;

  const firstSession = parseSessionDate(meeting?.workshop_first_session_date || '');
  if (!firstSession) {
    const day = meeting?.schedule_day || 'Saturday';
    return `First meeting: ${day}, ${timePart} (date TBD)`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (today < firstSession) {
    return `First meeting: ${formatSessionDate(firstSession)} · ${timePart}`;
  }

  const nextMeeting = getNextMeetingDate(meeting, today) || firstSession;
  return `Next meeting: ${formatSessionDate(nextMeeting)} · ${timePart}`;
}

export function formatFirstSessionDate(meeting: Meeting | null): string {
  const dateStr = meeting?.workshop_first_session_date;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return 'date TBD';
  }
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'date TBD';
  }
}

function formatTime(startTime: string | undefined): string {
  if (!startTime || !/^\d{1,2}:\d{2}$/.test(startTime)) return '';
  const [h, m] = startTime.split(':').map(Number);
  const hour = h % 12 || 12;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatTzAbbrev(tz: string | undefined): string {
  if (!tz) return '';
  const parts = tz.split('/');
  const last = parts[parts.length - 1];
  const known: Record<string, string> = {
    Los_Angeles: 'Pacific',
    New_York: 'Eastern',
    Chicago: 'Central',
    Denver: 'Mountain',
  };
  return known[last] || last.replace('_', ' ');
}
